const getApiBaseUrl = () => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    // When on localhost, hit the local API
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:3000/api";
    }
    // When on Vercel, use the relative path (Vercel will proxy this)
    return "/api";
  }
  return "https://api.ospra.co/api";
};

export const API_BASE_URL = getApiBaseUrl();

type ResponseType = "json" | "blob" | "text" | "arrayBuffer";

interface FetchOptions extends RequestInit {
  responseType?: ResponseType;
}

export interface HttpProps {
  url: string;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  options?: FetchOptions;
}

const smartParse = (res: Response) => {
  const ct = res.headers.get("Content-Type") ?? "";
  if (ct.includes("application/json")) {
    return res.json();
  }
  if (ct.includes("text/")) {
    return res.text();
  }
  // PDF, Excel, images, etc.
  return res.blob();
};

const base = async <T = unknown>(
  method: string,
  { url, query, body, options = {} }: HttpProps
): Promise<T> => {
  const { responseType = "json", ...fetchOptions } = options;

  let finalUrl = `${API_BASE_URL}${url}`;

  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    }
    const queryString = params.toString();
    if (queryString) {
      finalUrl += (finalUrl.includes("?") ? "&" : "?") + queryString;
    }
  }

  const res = await fetch(finalUrl, {
    ...fetchOptions,
    method,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
    headers: {
      ...(responseType === "json" && { "Content-Type": "application/json" }),
      "x-org-id": localStorage.getItem("orgId") ?? "",
      ...fetchOptions.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw err;
  }

  return smartParse(res);
};

export const https = {
  get: <T = unknown>(props: HttpProps) => base<T>("GET", props),
  post: <T = unknown>(props: HttpProps) => base<T>("POST", props),
  put: <T = unknown>(props: HttpProps) => base<T>("PUT", props),
  delete: <T = unknown>(props: HttpProps) => base<T>("DELETE", props),
  patch: <T = unknown>(props: HttpProps) => base<T>("PATCH", props),
};
